import React from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { NewsWithViewStatus } from '../../types/news';
import { Badge } from '../Badge/Badge';
import { CardV2, CardContent, CardHeader, CardTitle } from '../CardV2/CardV2';

interface NewsItemProps {
  news: NewsWithViewStatus;
  onMarkAsViewed: (newsId: number) => void;
  onViewDetails: (news: NewsWithViewStatus) => void;
}

export const NewsItem: React.FC<NewsItemProps> = ({ 
  news, 
  onMarkAsViewed, 
  onViewDetails 
}) => {
  const { t } = useTranslation();
  
  const handleClick = () => {
    if (!news.viewed) {
      onMarkAsViewed(news.id);
    }
    onViewDetails(news);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return t('news.invalid_date') || 'Invalid date';
    }
  };

  return (
    <CardV2 
      className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
        !news.viewed ? 'border-l-4 border-l-blue-500 bg-blue-50' : ''
      }`}
      onClick={handleClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <CardTitle className="text-sm font-semibold text-gray-900 line-clamp-2">
            {news.title}
          </CardTitle>
          <div className="flex items-center space-x-2">
            {!news.viewed && (
              <Badge variant="secondary" className="text-xs">
                {t('news.new_badge') || 'New'}
              </Badge>
            )}
            <span className="text-xs text-gray-500">
              {formatDate(news.date)}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-sm text-gray-600 line-clamp-3">
          {news.content}
        </p>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {news.viewed 
              ? (t('news.click_to_view') || 'Click to view details')
              : (t('news.click_to_mark_read') || 'Click to mark as read')
            }
          </span>
        </div>
      </CardContent>
    </CardV2>
  );
};
